/**
 * Link export subtitles to video content: when "clip to subtitle" is on, the caption
 * stays anchored to a point on the source media and moves with pan/zoom.
 */

import {
  computeExportFramingLayout,
  normalizeVideoExportFraming,
} from '@/lib/videoExportFraming';

function clamp01(n, fb) {
  const v = Number(n);
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fb;
}

/** Layout + frame/media sizes for coordinate conversion. */
export function buildExportFramingMetrics(frameW, frameH, mediaW, mediaH, framing) {
  const fw = Math.max(1, Number(frameW) || 1);
  const fh = Math.max(1, Number(frameH) || 1);
  const mw = Math.max(1, Number(mediaW) || 16);
  const mh = Math.max(1, Number(mediaH) || 9);
  const lay = computeExportFramingLayout(fw, fh, mw, mh, framing);
  return {
    frameW: fw,
    frameH: fh,
    mediaW: mw,
    mediaH: mh,
    ...lay,
  };
}

/**
 * Capture where the subtitle sits on the source image/video for the current framing.
 */
export function captureSubtitleMediaAnchor(layer, framing, metrics) {
  const m = buildExportFramingMetrics(
    metrics.frameW,
    metrics.frameH,
    metrics.mediaW,
    metrics.mediaH,
    framing,
  );

  const cx = clamp01(layer?.x, 0.5) * m.frameW;
  const cy = clamp01(layer?.y, 0.82) * m.frameH;
  const boxW = Math.max(8, clamp01(layer?.width, 0.86) * m.frameW);
  const boxH = Math.max(8, clamp01(layer?.height, 0.14) * m.frameH);

  const ux = m.width > 0 ? (cx - m.left) / m.width : 0.5;
  const uy = m.height > 0 ? (cy - m.top) / m.height : 0.82;
  const uw = m.width > 0 ? boxW / m.width : 0.5;
  const uh = m.height > 0 ? boxH / m.height : 0.12;

  return {
    ux: clamp01(ux, 0.5),
    uy: clamp01(uy, 0.82),
    uw: Math.max(0.08, Math.min(1.2, uw)),
    uh: Math.max(0.04, Math.min(0.6, uh)),
    rotation: Number(layer?.rotation) || 0,
    textAlign: layer?.textAlign || 'center',
  };
}

/**
 * Map a media anchor back to frame-normalized subtitle layer coords for this framing.
 */
export function subtitleLayerFromMediaAnchor(anchor, framing, metrics, baseLayer = {}) {
  const m = buildExportFramingMetrics(
    metrics.frameW,
    metrics.frameH,
    metrics.mediaW,
    metrics.mediaH,
    framing,
  );

  const a = anchor || {};
  const cx = m.left + clamp01(a.ux, 0.5) * m.width;
  const cy = m.top + clamp01(a.uy, 0.82) * m.height;
  const boxW = Math.max(8, (a.uw || 0.5) * m.width);
  const boxH = Math.max(8, (a.uh || 0.12) * m.height);

  return {
    ...baseLayer,
    x: clamp01(cx / m.frameW, 0.5),
    y: clamp01(cy / m.frameH, 0.82),
    width: clamp01(boxW / m.frameW, 0.86),
    height: clamp01(boxH / m.frameH, 0.14),
    rotation: Number(a.rotation) || 0,
    textAlign: a.textAlign || baseLayer.textAlign || 'center',
  };
}

/** Sync subtitle layer after framing changed while clip-to-subtitle is active. */
export function syncSubtitleLayerWithExportFraming(anchor, framing, metrics, baseLayer) {
  if (!anchor) return baseLayer;
  return subtitleLayerFromMediaAnchor(anchor, framing, metrics, baseLayer);
}
